import { dayjs } from '#gp/app-common/util/dayjs';
import { gpCommon } from '../app';
import { IAuthorisationStatus, IAuthorisationCheckerService, setGameplanTokenCookie, IAuthorisationCheckerService_serviceName, } from './authorisation-checker-with-caching-service';
import { IStorageService } from './local-storage-service';
import { ILogglyService } from './jsloggly-service';

export interface ILoginResponse {
    access_token: string;
    token_type: string;
}

export interface ILoginInputs {
    email: string;
    password: string;
}

interface IEmailAndToken {
    email: string;
    token: string;
}

export class LoginService {
    public static readonly serviceName = "gpLoginService"
    static $inject = ['$http', '$q', 'gpLocalStorageService', IAuthorisationCheckerService_serviceName, 'gpLogglyService'];
    private serviceBase = __gameplan_service_url || 'http://localhost/';

    constructor(
        private readonly $http: ng.IHttpService,
        private readonly $q: ng.IQService,
        private readonly storage: IStorageService,
        private readonly authChecker: IAuthorisationCheckerService,
        private readonly gpLogglyService: ILogglyService
    ) {
    }

    public login = (loginData: ILoginInputs): ng.IPromise<ILoginResponse> => {
        this.gpLogglyService.information("Logging in to gameplan client {UserEmail}", [loginData.email], { ClientContext: LoginService.serviceName });

        const params = {
            grant_type: "password",
            username: loginData.email,
            password: loginData.password,
        };

        const deferred = this.$q.defer<ILoginResponse>();
        setGameplanTokenCookie('');

        this.$http<ILoginResponse>({
            method: 'POST',
            url: `${this.serviceBase}oauth/token`,
            data: $.param(params),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }).then(response => {
            if (!response.data) {
                throw new Error('Invalid response');
            }

            this.gpLogglyService.information("Log in to gameplan client {UserEmail} successful", [loginData.email], { ClientContext: "gpLoginService" });

            this.storage.setAsObject('loginInfo', {
                username: loginData.email,
                timestamputc: dayjs.utc().toISOString(),
            });

            this.saveAuthorisationData({
                token: response.data.access_token,
                email: loginData.email,
            });

            this.authChecker.resetCheckedStatus();
            this.checkAuthorisationIfNeeded();

            deferred.resolve(response.data);

        }).catch(response => {
            this.gpLogglyService.information("Log in to gameplan client {UserEmail} failed {ErrorHttpStatus} {Error}", [loginData.email, response.status, response], { ClientContext: "gpLoginService" });
            this.logOut();

            deferred.reject(response);
        });

        deferred.promise.catch(err => {
            // catch this here just so errors in the console appear about unhandled rejections
        });

        return deferred.promise;
    }

    public logOut() {
        const authData = this.loadAuthorisationData();
        this.gpLogglyService.information("Logging out of gameplan client {UserEmail}", [authData.email], { ClientContext: "gpLoginService" });

        this.storage.setAsObject('logoutInfo', {
            username: authData.email,
            timestamputc: dayjs.utc().toISOString(),
        });

        setGameplanTokenCookie('');
        this.authChecker.resetCheckedStatus();

        this.storage.remove('authorizationData');

        this.saveAuthorisationData({ email: "", token: "", });
        this.authChecker.resetCheckedStatus();
    }

    private saveAuthorisationData(data: IEmailAndToken) {
        setGameplanTokenCookie(data.token);
        this.storage.setAsObject('authorizationData', {
            token: data.token,
            email: data.email
        });

    }

    private loadAuthorisationData(): IEmailAndToken {

        let authData = this.storage.getAsObject<IEmailAndToken>('authorizationData');
        if (authData) {
            return {
                email: authData.email,
                token: authData.token,
            }
        } else {
            return {
                email: "",
                token: "",
            }
        }
    }

    public checkAuthorisationIfNeeded(): ng.IPromise<IAuthorisationStatus> {
        return this.authChecker.checkAuthorisationIfNeeded();
    }

    public authorisationChanged(callback: ((authStatus: IAuthorisationStatus) => void)): void {
        this.authChecker.authorisationChanged(callback);
    }

    public currentUsername(): string {
        const result = this.loadAuthorisationData();
        return result.email;
    }

    public currentToken(): string {
        const result = this.loadAuthorisationData();
        return result.token;
    }
}

export const gpLoginService_ServiceName = LoginService.serviceName;
export type gpLoginService = LoginService;

gpCommon.service(gpLoginService_ServiceName, LoginService);

